import {i18n, initReactI18next} from '@nlabs/gothamui/i18n';
import {createRoot} from 'react-dom/client';

import {getGothamUiRedirect} from './lib/gothamUiRedirect.js';
import {isPoohHost} from './lib/pooh.js';
import {isTwasHost} from './lib/twas.js';
import {manageExternalLinks} from './utils/externalLinks.js';

import type {ComponentType} from 'react';

const appContainer = document.getElementById('app');

if(!appContainer) {
  throw new Error('Missing #app root container');
}

if(!i18n.isInitialized) {
  await i18n
    .use(initReactI18next)
    .init({
      fallbackLng: 'en',
      interpolation: {
        escapeValue: false
      },
      lng: 'en',
      resources: {
        en: {
          translation: {}
        }
      }
    });
}

if(isPoohHost(window.location.hostname, window.location.pathname)) {
  const {PoohApp} = await import('./microsites/pooh/PoohApp');
  createRoot(appContainer).render(<PoohApp />);
} else if(isTwasHost(window.location.hostname, window.location.pathname)) {
  const {TwasApp} = await import('./microsites/twas/TwasApp');
  createRoot(appContainer).render(<TwasApp />);
} else {
  await import('./styles/main.css');
  const legacyRedirect = getGothamUiRedirect(window.location);
  if(legacyRedirect?.startsWith('https://')) {
    window.location.replace(legacyRedirect);
  } else if(legacyRedirect) {
    window.history.replaceState(window.history.state, '', legacyRedirect);
  }

  const isArkhamDocsHost = window.location.hostname === 'arkhamjs.nitrogenx.co';
  const isGothamDocsHost = window.location.hostname === 'gothamui.nitrogenx.co';
  const isLexDocsHost = window.location.hostname === 'lex.nitrogenx.co';
  const isMetropolisDocsHost = window.location.hostname === 'metropolisjs.nitrogenx.co';
  const isReaktorDocsHost = window.location.hostname === 'reaktor.nitrogenx.co';
  const isReaktorDocsExperience = isReaktorDocsHost || window.location.pathname === '/reaktor/docs' || window.location.pathname.startsWith('/reaktor/docs/');
  let RootComponent: ComponentType;
  if(isArkhamDocsHost) {
    RootComponent = (await import('./views/ArkhamRouter/ArkhamRouter')).ArkhamRouter;
  } else if(isGothamDocsHost) {
    RootComponent = (await import('./microsites/gothamui/GothamDocsApp')).GothamDocsApp;
  } else if(isLexDocsHost) {
    RootComponent = (await import('./views/Lex/Lex')).Lex;
  } else if(isMetropolisDocsHost) {
    RootComponent = (await import('./microsites/metropolisjs/MetropolisDocsApp')).MetropolisDocsApp;
  } else if(isReaktorDocsHost) {
    RootComponent = (await import('./microsites/reaktor/ReaktorDocsApp')).ReaktorDocsApp;
  } else {
    RootComponent = (await import('./components/App')).App;
  }

  const demoOnly = window.location.pathname === '/alfred/demo' || (
    window.location.hostname === 'alfred.nitrogenx.co' && window.location.pathname === '/demo'
  );
  const {AskNitrogen} = await import('./components/AskNitrogen/AskNitrogen');
  manageExternalLinks(appContainer);
  if(isReaktorDocsExperience) {
    const {ReaktorAnalyticsProvider} = await import('./components/ReaktorAnalyticsProvider/ReaktorAnalyticsProvider');
    createRoot(appContainer).render(
      <ReaktorAnalyticsProvider><RootComponent />{demoOnly ? null : <AskNitrogen />}</ReaktorAnalyticsProvider>
    );
  } else {
    const {NitrogenMetropolisProvider} = await import('./components/NitrogenMetropolisProvider/NitrogenMetropolisProvider');
    createRoot(appContainer).render(
      <NitrogenMetropolisProvider><RootComponent />{demoOnly ? null : <AskNitrogen />}</NitrogenMetropolisProvider>
    );
  }
}
